import { Service } from "typedi";
import { PUBLIC_EVENTS } from "../../../../../application/core/models/constants/EventTypes";
import { CONTROL_FRAME_IFRAME } from "../../../../../application/core/models/constants/Selectors";
import { IMessageBusEvent } from "../../../../../application/core/models/IMessageBusEvent";
import { DomMethods } from "../../../../../application/core/shared/dom-methods/DomMethods";
import { IFrameQueryingService } from "../../../../../shared/services/message-bus/interfaces/IFrameQueryingService";
import { IApplePayProcessPaymentResponse } from "../../../application/services/apple-pay-payment-service/IApplePayProcessPaymentResponse";
import { IApplePayGatewayRequest } from "../../../models/IApplePayRequest";
import { IApplePaySession } from "../../models/IApplePaySession";

import { APPLE_PAY_STATUS } from "../session/models/ApplePayStatus";
import "./PaymentAuthorization.scss";
import { ApplePayOverlayService } from "./ApplePayOverlayService";
import { IApplePayPaymentDataCallBacks } from "../../../models/IApplePayPaymentDataCallbacks";
import { ApplePayPaymentAuthorizationResolver } from "./ApplePayPaymentAuthorizationResolver";
import { RequestJwtUpdateHandler } from "../../../../../application/core/services/st-codec/RequestJwtUpdateHandler";
import { IMessageBus } from "../../../../../application/core/shared/message-bus/IMessageBus";
import { EventScope } from "../../../../../application/core/models/constants/EventScope";
import { APPLE_PAY_PAYMENT_METHOD_NAME } from "../../../models/IApplePayPaymentMethod";

@Service()
export class ApplePayPaymentAuthorizationAction {
  constructor(
    private readonly frameQueryingService: IFrameQueryingService,
    private readonly overlayService: ApplePayOverlayService,
    private readonly jwtRequestHandler: RequestJwtUpdateHandler,
    private readonly messageBus: IMessageBus,
  ) {}

  execute(
    applePaySession: IApplePaySession,
    formId: string,
    event: ApplePayJS.ApplePayPaymentAuthorizedEvent,
    callbacks?: IApplePayPaymentDataCallBacks,
  ) {
    const processPayment = (
      result?: ApplePayJS.ApplePayPaymentAuthorizationResult,
    ) => {
      if (!result || result.status === APPLE_PAY_STATUS.STATUS_SUCCESS) {
        const applePayStFormElement = document.getElementById("st-form");
        if (applePayStFormElement) {
          this.overlayService.show(applePayStFormElement);
        }

        const formData = DomMethods.parseForm(formId);

        const paymentAuthorizedQueryEvent: IMessageBusEvent<IApplePayGatewayRequest> =
          {
            type: PUBLIC_EVENTS.APPLE_PAY_AUTHORIZATION_2,
            data: {
              ...formData,
              walletsource: "APPLEPAY",
              wallettoken: JSON.stringify(event?.payment),
              termurl: "https://termurl.com",
            },
          };

        // send query to application side
        this.frameQueryingService
          .query(paymentAuthorizedQueryEvent, CONTROL_FRAME_IFRAME)
          .subscribe({
            next: (response: unknown) => {
              const responseObj: IApplePayProcessPaymentResponse =
                response as IApplePayProcessPaymentResponse;
              this.overlayService.hide();
              if (Number(responseObj.errorcode) === 0) {
                applePaySession.completePayment({
                  status: APPLE_PAY_STATUS.STATUS_SUCCESS,
                });
              } else {
                applePaySession.completePayment({
                  status: APPLE_PAY_STATUS.STATUS_FAILURE,
                });
              }
            },
            error: () => {
              this.overlayService.hide();
              applePaySession.completePayment({
                status: APPLE_PAY_STATUS.STATUS_FAILURE,
              });
            },
          });
        return;
      }

      this.messageBus.publish(
        {
          type: PUBLIC_EVENTS.PAYMENT_METHOD_FAILED,
          data: { name: APPLE_PAY_PAYMENT_METHOD_NAME },
        },
        EventScope.EXPOSE_TO_MERCHANT,
      );
      applePaySession.completePayment(result);
    };

    if (callbacks && callbacks?.onPaymentAuthorized) {
      const isResolverActionExist = callbacks.onPaymentAuthorized.length >= 2;

      if (isResolverActionExist) {
        const actions = new ApplePayPaymentAuthorizationResolver(
          processPayment,
          this.jwtRequestHandler,
        );

        try {
          callbacks.onPaymentAuthorized(event, actions);
        } catch (error) {
          // Proceed with payment processing even if callback throws error
          processPayment();
        }
      } else {
        try {
          callbacks.onPaymentAuthorized(event);
        } catch (error) {
          // Continue with payment processing even if legacy callback throws error
        }
        processPayment();
      }
    } else {
      processPayment();
    }
  }
}
